import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Button,
  Container,
} from "@mui/material";
import { LoadingSpinner } from "../components";
import { authService } from "@catalog/supabase";

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the current session after OAuth redirect
        const session = await authService.getCurrentSession();

        if (!session?.user) {
          setError("No user session found after authentication.");
          setIsLoading(false);
          return;
        }

        // Check if the user's email is approved
        const approvalResult = await authService.handlePostAuthApproval(
          session.user
        );

        if (approvalResult.approved) {
          // Redirect to the main app
          navigate("/", { replace: true });
        } else {
          setError(
            "Your email address is not approved for access. Please contact the administrator."
          );
          setIsLoading(false);
        }
      } catch (err: any) {
        console.error("Auth callback error:", err);
        setError(err.message || "An error occurred during authentication.");
        setIsLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  const handleReturnToLogin = () => {
    navigate("/login", { replace: true });
  };

  if (isLoading) {
    return <LoadingSpinner fullHeight message="Completing authentication..." />;
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Card sx={{ width: "100%", maxWidth: 400 }}>
            <CardContent sx={{ p: 4 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Authentication Failed
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>

              <Button
                fullWidth
                variant="contained"
                onClick={handleReturnToLogin}
                sx={{ mt: 2 }}
              >
                Return to Login
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return null;
};

export default AuthCallback;
